import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Button } from '../shared';
import ModalShell from '../shared/ModalShell';
import { useToastContext } from '../../contexts/ToastContext';

const EventEndModal = ({
 isOpen,
 onClose,
 onEnd,
 isLoading,
 eventPassed = false,
 willAutoStart = false,
}) => {
 const { showToast } = useToastContext();
 const [notes, setNotes] = useState('');
 const onCloseRef = useRef(onClose);

 useEffect(() => {
 onCloseRef.current = onClose;
 }, [onClose]);

 useEffect(() => {
 if (!isOpen) {
 setNotes('');
 }
 }, [isOpen]);

 const handleClose = () => {
 setNotes('');
 onCloseRef.current();
 };

 const handleSubmit = async (e) => {
 e.preventDefault();

 try {
 const result = onEnd({
 notes: notes.trim() || null,
 screenshots: null,
 });

 if (result && typeof result.then === 'function') {
 await result;
 }
 } catch (error) {
 console.error('Error ending event:', error);
 showToast(
 error?.response?.data?.message || error?.message || 'Could not end event',
 'error'
 );
 }
 };

 return (
 <ModalShell
 isOpen={isOpen}
 onClose={handleClose}
 labelledBy="event-end-title"
 panelClassName="max-w-2xl"
 >
 <div className="bg-surface rounded-lg border border-border shadow-sm overflow-hidden">
 <div className="sticky top-0 bg-surface border-b border-border px-6 py-4 flex items-center justify-between z-10">
 <h2 id="event-end-title" className="text-xl font-bold text-ink">
 End Event
 </h2>
 <button
 type="button"
 onClick={handleClose}
 className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-surface-muted"
 aria-label="Close"
 >
 <X className="w-6 h-6" />
 </button>
 </div>

 <form onSubmit={handleSubmit} className="p-6 space-y-6">
 <div
 className={`rounded-xl border p-4 ${ eventPassed ? 'border-amber-200 bg-amber-50' : 'border-border bg-surface-muted' }`}
 >
 <p
 className={`text-sm leading-relaxed ${ eventPassed ? 'text-amber-900' : 'text-ink' }`}
 >
 {eventPassed ? (
 <>
 <strong>Event date has passed.</strong> Ending now marks this booking as completed
 {willAutoStart ? ' and records start + end for your timeline' : ''}.
 </>
 ) : (
 <>
 <strong>Note:</strong> Ending the event updates the booking status to
 &quot;Completed&quot;.
 </>
 )}
 </p>
 </div>

 <div>
 <label className="block text-sm font-medium text-ink-secondary mb-2">
 Notes (Optional)
 </label>
 <textarea
 value={notes}
 onChange={(e) => setNotes(e.target.value)}
 rows={4}
 className="w-full px-3 py-2 border border-border bg-surface text-ink rounded-xl focus:ring-2 focus:ring-ink/40 focus:border-ink"
 placeholder="Add any notes about ending the event..."
 maxLength={2000}
 />
 <p className="mt-1 text-xs text-ink-tertiary">
 {notes.length}/2000 characters
 </p>
 </div>

 <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
 <Button
 type="button"
 variant="outline"
 onClick={handleClose}
 disabled={isLoading}
 className="rounded-md"
 >
 Cancel
 </Button>
 <Button
 type="submit"
 variant="primary"
 loading={isLoading}
 disabled={isLoading}
 className="rounded-md"
 >
 End Event
 </Button>
 </div>
 </form>
 </div>
 </ModalShell>
 );
};

export default EventEndModal;
